import { ArrowUpRight, Building2, Settings2, Users, CheckCircle2, ClipboardCheck } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { CALENDLY_URL } from "../lib/site";
import Tm from "../components/ow/Tm";
import Seo from "../components/Seo";
import { businessSchema } from "../lib/seoSchemas";
import { usePricing } from "../lib/pricing";
import { useContent } from "../lib/content";

const services = [
  {
    id: "fractional",
    icon: Building2,
    eyebrow: "Service 01",
    k: "services.s1",
    bullets: [
      "P&L oversight and operational strategy",
      "Team development and leadership coaching",
      "Vendor management and procurement",
      "Compliance and regulatory oversight",
      "Board and investor reporting",
    ],
    pricing: [
      { label: "Fractional COO", key: "fractional_coo" },
      { label: "Interim President", key: "interim_president" },
      { label: "Advisory and Project", key: "advisory" },
    ],
  },
  {
    id: "operations",
    icon: Settings2,
    eyebrow: "Service 02",
    k: "services.s2",
    bullets: [
      "Food and labor cost optimization",
      "Operational workflow design",
      "Technology stack implementation",
      "Multi-unit systems and playbook development",
      "Compliance and regulatory management",
      "Performance benchmarking and reporting",
    ],
  },
  {
    id: "people",
    icon: Users,
    eyebrow: "Service 03",
    k: "services.s3",
    bullets: [
      "Fractional HR leadership",
      "Retained and contingent talent acquisition, front-line to executive",
      "Onboarding and training program design",
      "Compensation and performance framework development",
      "Organizational design and workforce planning",
      "Employment handbook and policy development",
    ],
    pricing: [
      { label: "Retained Executive Search", key: "retained_executive_search" },
      { label: "Contingent Placement", key: "contingent_placement" },
      { label: "Fractional HR Leadership", key: "fractional_hr" },
    ],
  },
];

export default function Services() {
  const { getDisplay } = usePricing();
  const { t } = useContent();
  const { hash } = useLocation();
  const owBase = getDisplay("operator_walk_base");
  const owAdditional = getDisplay("operator_walk_additional_location");

  // Smooth-scroll to a section when arriving via a /services#section link.
  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el) {
      requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
  }, [hash]);

  return (
    <div data-testid="page-services">
      <Seo
        title={t("services.seo.title")}
        description={t("services.seo.description")}
        schemas={[businessSchema]}
      />
      {/* HERO */}
      <section className="bg-white py-20 lg:py-28 border-b border-ink-line" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="eyebrow mb-6">{t("services.hero.eyebrow")}</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-brand-700 leading-[1.02] tracking-tight max-w-4xl">
            {t("services.hero.heading")}
          </h1>
          <p className="mt-8 text-lg text-ink-soft max-w-2xl leading-relaxed">
            {t("services.hero.intro")}
          </p>
        </div>
      </section>

      {/* SERVICES */}
      <div className="bg-white">
        {services.map((s, idx) => {
          const Icon = s.icon;
          const alt = idx % 2 === 1;
          return (
            <section
              key={s.id}
              id={s.id}
              className={`py-24 lg:py-28 ${alt ? "bg-brand-100" : "bg-white"}`}
              data-testid={`service-section-${s.id}`}
            >
              <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                  <div className="lg:col-span-5">
                    <div className="flex items-center gap-4 mb-8">
                      <Icon className="w-8 h-8 text-brand-700" />
                      <div className="text-[10px] tracking-[0.25em] uppercase text-brand-500">
                        {s.eyebrow}
                      </div>
                    </div>
                    <h2 className="font-serif text-4xl sm:text-5xl text-brand-700 leading-tight">
                      {t(`${s.k}.title`)}
                    </h2>
                    <p className="mt-6 text-base lg:text-lg text-ink-soft leading-relaxed">
                      {t(`${s.k}.lead`)}
                    </p>
                    <a
                      href={CALENDLY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-10 inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-7 py-4 text-sm font-medium transition-colors group"
                      data-testid={`service-cta-${s.id}`}
                    >
                      Book a Call
                      <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                  </div>

                  <div className="lg:col-span-7 lg:pl-8 lg:border-l lg:border-brand-200">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-brand-500 mb-6">
                      Scope
                    </div>
                    <ul className="space-y-4">
                      {s.bullets.map((b) => (
                        <li
                          key={b}
                          className="flex items-start gap-4 text-base text-ink-soft"
                        >
                          <CheckCircle2 className="w-5 h-5 text-brand-700 shrink-0 mt-0.5" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>

                    {s.pricing && (() => {
                      const visible = s.pricing
                        .map((p) => ({ ...p, display: getDisplay(p.key) }))
                        .filter((p) => p.display);
                      if (visible.length === 0) return null;
                      return (
                        <div className="mt-12">
                          <div className="text-[10px] tracking-[0.25em] uppercase text-brand-500 mb-6">
                            Retainer Pricing
                          </div>
                          <div className="border border-brand-200 bg-white">
                            {visible.map((p, i) => (
                              <div
                                key={p.key}
                                className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-6 py-5 ${
                                  i < visible.length - 1 ? "border-b border-brand-200" : ""
                                }`}
                                data-testid={`service-price-${p.key}`}
                              >
                                <div className="font-serif text-xl text-brand-700">
                                  {p.label}
                                </div>
                                <div className="text-ink-soft text-sm sm:text-base">
                                  {p.display}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      {/* SERVICE 04: THE OPERATOR WALK */}
      <section id="operator-walk" className="bg-white py-24 lg:py-28 border-t border-ink-line" data-testid="service-section-operator-walk">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-4 mb-8">
                <ClipboardCheck className="w-8 h-8 text-brand-700" />
                <div className="text-[10px] tracking-[0.25em] uppercase text-brand-500">Service 04</div>
              </div>
              <h2 className="font-serif text-4xl sm:text-5xl text-brand-700 leading-tight">{t("services.s4.title")}<Tm /></h2>
              <p className="mt-6 text-base lg:text-lg text-ink-soft leading-relaxed max-w-2xl">
                {t("services.s4.lead")}
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/services/operator-walk"
                  className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-7 py-4 text-sm font-medium transition-colors group"
                  data-testid="service-operator-walk-link"
                >
                  {t("services.s4.link")}
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <Link
                  to="/portal/login"
                  className="inline-flex items-center gap-2 text-brand-700 text-sm font-medium tracking-wide border-b border-brand-700 pb-1 hover:border-brand-400 transition-colors"
                  data-testid="service-operator-walk-login"
                >
                  Client login
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 lg:pl-8 lg:border-l lg:border-brand-200">
              {owBase && (
                <div className="text-[10px] tracking-[0.25em] uppercase text-brand-500 mb-6">From {owBase}</div>
              )}
              <div className="border border-brand-200 bg-white">
                {owBase && (
                  <div className="px-6 py-5 border-b border-brand-200 flex items-center justify-between gap-2" data-testid="service-ow-base">
                    <div className="font-serif text-xl text-brand-700">Up to three locations</div>
                    <div className="text-ink-soft text-sm">{owBase}</div>
                  </div>
                )}
                {owAdditional && (
                  <div className="px-6 py-5 flex items-center justify-between gap-2" data-testid="service-ow-additional">
                    <div className="font-serif text-xl text-brand-700">Each additional location</div>
                    <div className="text-ink-soft text-sm">{owAdditional}</div>
                  </div>
                )}
              </div>
              <p className="mt-4 text-sm text-ink-muted">{t("services.s4.note")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-brand-700 text-white py-20" data-testid="services-final-cta">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h2 className="font-serif text-3xl sm:text-4xl leading-tight">
              {t("services.cta.heading")}
            </h2>
            <p className="mt-4 text-brand-100/90 max-w-xl">
              {t("services.cta.body")}
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-brand-700 hover:bg-brand-100 transition-colors px-7 py-4 text-sm font-medium"
              data-testid="services-cta-button"
            >
              {t("services.cta.button")} <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
